import React, { useContext, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Hotel, LocationOn, OpenInNew } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import API from '../../../services/API'
import { TreatmentCentreForm } from '../ControlRoom/ControlPage/TreatmentCentres/Configuration'
import { UserContext } from '../../../App'

const TreatmentLocationCard = ({ location }) => {
  const navigate = useNavigate()
  const canEnter = location.canEnter !== false
  const roomTitle =
    location.controlRoomInfo?.linkedEvent?.title ||
    location.controlRoomInfo?.customTitle ||
    `Control Room ${location.controlRoomInfo?.controlRoomRef ?? ''}`.trim()

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid rgba(15,23,42,0.08)',
        boxShadow: '0 10px 28px rgba(15,23,42,0.08)',
        flex: 1,
        // noWrap text inside must not widen the card past its grid cell
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      <Stack
        spacing={1.5}
        justifyContent="space-between"
        height="100%"
        sx={{ minWidth: 0 }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#E6F7F9',
                color: '#1E7F88',
              }}
            >
              <Hotel />
            </Box>

            <Box minWidth={0}>
              <Typography fontWeight={900} noWrap>
                {location.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {location.type || 'Treatment Location'}
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            flexWrap="wrap"
            useFlexGap
            sx={{ minWidth: 0, '& .MuiChip-root': { maxWidth: '100%' } }}
          >
            <Chip
              size="small"
              label={location.status || 'Open'}
              color={location.status === 'Open' ? 'success' : 'default'}
              sx={{ borderRadius: 999, fontWeight: 800 }}
            />
            <Chip
              size="small"
              label={canEnter ? 'Assigned' : 'Not assigned'}
              color={canEnter ? 'info' : 'warning'}
              sx={{ borderRadius: 999, fontWeight: 800 }}
            />
            {roomTitle && (
              <Chip
                size="small"
                label={roomTitle}
                sx={{ borderRadius: 999, fontWeight: 800 }}
              />
            )}
          </Stack>

          {location.location && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ minWidth: 0 }}
            >
              <LocationOn fontSize="small" color="action" />
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ minWidth: 0 }}
              >
                {location.location}
              </Typography>
            </Stack>
          )}

          <Box flex={1} />
        </Stack>
        <Stack spacing={1}>
          <Button
            variant="contained"
            endIcon={<OpenInNew />}
            onClick={() => navigate(`/treatmentlocations/${location._id}`)}
            disabled={!canEnter}
            sx={{
              borderRadius: 999,
              textTransform: 'none',
              fontWeight: 900,
              backgroundColor: '#239AA4',
            }}
          >
            {canEnter ? 'Open Location' : 'Assigned staff only'}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}

const TreatmentLocations = () => {
  const navigate = useNavigate()
  const { user } = useContext(UserContext)
  const [locations, setLocations] = useState([])
  const [activeRooms, setActiveRooms] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingRooms, setLoadingRooms] = useState(true)
  const [canManageLocations, setCanManageLocations] = useState(false)
  const [openRoomPicker, setOpenRoomPicker] = useState(false)
  const [openCreateForm, setOpenCreateForm] = useState(false)
  const [selectedControlRoom, setSelectedControlRoom] = useState('')

  useEffect(() => {
    if (user?.employee?.cadAccess === 'Commander') {
      setCanManageLocations(true)
    }
  }, [user])

  const roomTitle = (room) =>
    room?.linkedEvent?.title ||
    room?.customTitle ||
    `Control Room ${room?.controlRoomRef ?? ''}`.trim()

  const loadLocations = async (active = true) => {
    setLoading(true)

    try {
      const res = await API.get('/commandcontrol/my-treatment-locations')

      if (active && res.status === 200) {
        setLocations(res.data || [])
      }
    } finally {
      if (active) setLoading(false)
    }
  }

  const loadActiveRooms = async (active = true) => {
    setLoadingRooms(true)
    try {
      const res = await API.get('/controlroominfo/dashboard/activecontrolrooms')

      if (active && res.status === 200) {
        setActiveRooms(res.data || [])
      }
    } finally {
      if (active) setLoadingRooms(false)
    }
  }

  useEffect(() => {
    let active = true

    loadLocations(active)
    loadActiveRooms(active)

    return () => {
      active = false
    }
  }, [])

  const handleCreateLocation = () => {
    if (activeRooms.length === 1) {
      setSelectedControlRoom(activeRooms[0]._id)
      setOpenCreateForm(true)
      return
    }

    setSelectedControlRoom('')
    setOpenRoomPicker(true)
  }

  const handleRoomSelected = () => {
    if (!selectedControlRoom) return

    setOpenRoomPicker(false)
    setOpenCreateForm(true)
  }

  const handleLocationSaved = async () => {
    await loadLocations(true)
  }

  const canCreateLocation = canManageLocations && activeRooms.length > 0

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={2.5}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'flex-start' }}
          spacing={2}
        >
          <Stack spacing={0.5}>
            <Typography variant="h4" fontWeight={900}>
              Treatment Locations
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active medical centres and treatment areas. You can enter
              locations you are assigned to.
            </Typography>
          </Stack>

          {(canManageLocations || loadingRooms) && (
            <Stack
              spacing={0.75}
              alignItems={{ xs: 'stretch', sm: 'flex-end' }}
            >
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateLocation}
                disabled={!canCreateLocation || loadingRooms}
                sx={{
                  borderRadius: 999,
                  textTransform: 'none',
                  fontWeight: 900,
                  backgroundColor: '#239AA4',
                }}
              >
                Create Treatment Location
              </Button>

              {!loadingRooms &&
                canManageLocations &&
                activeRooms.length === 0 && (
                  <Button
                    size="small"
                    onClick={() => navigate('/commandcontrol/controlroom')}
                    sx={{ textTransform: 'none', fontWeight: 800 }}
                  >
                    Open a control room first
                  </Button>
                )}
            </Stack>
          )}
        </Stack>

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid rgba(15,23,42,0.08)',
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading treatment locations...
              </Typography>
            </Stack>
          </Paper>
        ) : locations.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 3,
              border: '1px dashed rgba(15,23,42,0.18)',
              backgroundColor: '#F8FAFC',
              textAlign: 'center',
            }}
          >
            <Typography fontWeight={900}>
              No active treatment locations found.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Active locations will appear here, and assigned users can enter
              them.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {locations.map((location) => (
              <Grid
                item
                xs={12}
                md={6}
                lg={4}
                key={location._id}
                display="flex"
              >
                <TreatmentLocationCard location={location} />
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>

      <Dialog
        open={openRoomPicker}
        onClose={() => setOpenRoomPicker(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Control Room</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Treatment locations must be linked to an active control room.
            </Typography>

            <TextField
              label="Active control room"
              select
              fullWidth
              value={selectedControlRoom}
              onChange={(e) => setSelectedControlRoom(e.target.value)}
            >
              {activeRooms.map((room) => (
                <MenuItem key={room._id} value={room._id}>
                  {roomTitle(room)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRoomPicker(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRoomSelected}
            disabled={!selectedControlRoom}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <TreatmentCentreForm
        open={openCreateForm}
        onClose={() => setOpenCreateForm(false)}
        controlRoomID={selectedControlRoom}
        centre={null}
        onSaved={handleLocationSaved}
      />
    </Box>
  )
}

export default TreatmentLocations
